import AsyncSelect from "@/components/ui/AsyncSelect";
import { UpdateAction } from "@/enums/common.enum";
import { usePropertySearch } from "@/hooks/usePropertySearch";
import { SearchLocation } from "@/interfaces/property.interface";
import clsx from "clsx";
import { LoaderIcon, SearchIcon, XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { CSSProperties } from "react";
import { components, PropsValue, SelectComponentsConfig } from "react-select";
import { useIsMounted } from "usehooks-ts";
import { CustomMenuList } from "./MenuList";
import { DropdownGroup, DropdownOption } from "./interface";

export interface PropertySearchInputProps {
  variant?: "small" | "large";
  onChange: (location: SearchLocation | null, action: UpdateAction) => void;
  value: PropsValue<DropdownOption> | undefined;
}

export const PropertySearchInput: React.FC<PropertySearchInputProps> = ({
  variant = "small",
  onChange,
  value,
}) => {
  const t = useTranslations("shared.search");
  const isInitialized = useIsMounted()();

  const {
    menuOpen,
    setMenuOpen,
    searchText,
    debouncedSearchText,
    isLoading,
    loadOptions,
    updateRecentlySearchedLocations,
    handleInputChange,
  } = usePropertySearch(isInitialized);

  const customComponents: SelectComponentsConfig<
    DropdownOption,
    true,
    DropdownGroup
  > = {
    DropdownIndicator: null,
    MultiValueRemove: ({ innerProps }) => (
      <div {...innerProps}>
        <XIcon className="text-gray-800 size-4 cursor-pointer" />
      </div>
    ),
    ClearIndicator: (props) => {
      const {
        getStyles,
        clearValue,
        innerProps: { ref, ...restInnerProps },
      } = props;

      return (
        <div
          {...restInnerProps}
          ref={ref}
          style={getStyles("clearIndicator", props) as CSSProperties}
          className="cursor-pointer"
          onClick={clearValue}
        >
          <XIcon className="text-gray-500" />
        </div>
      );
    },
    LoadingIndicator: ({ innerProps }) => (
      <div {...innerProps}>
        <LoaderIcon className="animate-spin size-4.5" />
      </div>
    ),
    MultiValue: (props) => {
      const { getValue, data, index } = props;
      const selectedOptions = getValue();

      if (menuOpen) {
        if (searchText.length === 0) {
          return (
            <p
              className={clsx("text-gray-500 absolute line-clamp-1", {
                "text-lg leading-[22.5px]": variant === "large",
                "text-base leading-5": variant === "small",
              })}
            >
              {t("placeholder")}
            </p>
          );
        }
        return null;
      }

      if (index < 1) {
        return (
          <components.MultiValue {...props}>
            <p className="!line-clamp-1 w-full truncate">{data.label}</p>
          </components.MultiValue>
        );
      }

      if (index === 1) {
        return (
          <components.MultiValue {...props}>
            + {selectedOptions.length - 1}
          </components.MultiValue>
        );
      }

      return null;
    },
    MenuList: (props) => (
      <CustomMenuList
        {...props}
        searchText={searchText}
        debouncedSearchText={debouncedSearchText}
        isLoading={isLoading}
        isInitialized={isInitialized}
        updateSearchedLocations={onChange}
        updateRecentlySearchedLocations={updateRecentlySearchedLocations}
      />
    ),
  };

  return (
    <div className="min-h-full w-full relative overflow-visible">
      <div className="h-full w-full absolute inset-0 overflow-visible">
        <div className="z-30 absolute left-2 rtl:right-1 -top-1 bottom-0 flex items-center pointer-events-none">
          <SearchIcon
            className={clsx("text-gray-500", {
              "size-6": variant === "large",
              "size-5": variant === "small",
            })}
          />
        </div>
        <div className="w-full h-full">
          <AsyncSelect
            isClearable={true}
            instanceId={`react-select-${isInitialized}`}
            onMenuOpen={() => setMenuOpen(true)}
            onMenuClose={() => setMenuOpen(false)}
            key={`${isInitialized}-value`}
            variant={variant}
            placeholder={t("placeholder")}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            inputValue={searchText}
            value={value}
            onChange={(value, meta) => {
              if (meta.action === "select-option") {
                onChange(
                  (value as SearchLocation[])[value.length - 1],
                  UpdateAction.ADD
                );
              } else if (meta.action === "remove-value") {
                onChange(
                  meta.removedValue as SearchLocation,
                  UpdateAction.REMOVE
                );
              } else if (meta.action === "clear") {
                onChange(null, UpdateAction.CLEAR);
              }
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            components={customComponents as any}
            debounceTimeout={300}
            menuIsOpen={menuOpen}
            loadOptions={loadOptions}
            isMulti={true}
            backspaceRemovesValue={true}
            closeMenuOnSelect={true}
          />
        </div>
      </div>
    </div>
  );
};
